import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiChevronRight,
  FiExternalLink,
  FiImage,
  FiMusic,
  FiSearch,
  FiVideo,
  FiX,
} from "react-icons/fi";
import {
  GetLinkedMediaDocument,
  type GetLinkedMediaQuery,
  type GetLinkedMediaQueryVariables,
  LinkMediaDocument,
  type LinkMediaMutation,
  type LinkMediaMutationVariables,
  type MediaType,
  SearchMediaDocument,
  type SearchMediaQuery,
  type SearchMediaQueryVariables,
  UnlinkMediaDocument,
  type UnlinkMediaMutation,
  type UnlinkMediaMutationVariables,
} from "../../generated/graphql";
import { getMediaConfig } from "./config";
import { ConfirmationModal } from "./modals";

interface MediaLinkSectionProps {
  sourceMediaId: string | null; // null for new uploads (not saved yet)
  mediaType: MediaType; // Type of media to link (Image or Video)
  onLink?: (selectedIds: string[]) => void; // For temporary linking (new uploads)
  onUnlink?: (linkId: string) => void;
  currentLinks?: string[]; // Currently linked IDs for new uploads (pending links)
}

export function MediaLinkSection({
  sourceMediaId,
  mediaType,
  onLink,
  onUnlink,
  currentLinks = [],
}: MediaLinkSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [linkIdToUnlink, setLinkIdToUnlink] = useState<string | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  // Query existing links (always fetch if sourceMediaId exists for badge count)
  const { data: linkedData, refetch: refetchLinks } = useQuery<
    GetLinkedMediaQuery,
    GetLinkedMediaQueryVariables
  >(GetLinkedMediaDocument, {
    variables: { mediaId: sourceMediaId || "" },
    skip: !sourceMediaId,
  });

  // Query pending links details for new uploads (when currentLinks has IDs)
  const { data: pendingLinksData } = useQuery<SearchMediaQuery, SearchMediaQueryVariables>(
    SearchMediaDocument,
    {
      variables: { query: "", type: mediaType },
      skip: !expanded || currentLinks.length === 0,
    }
  );

  // Query search results (only when expanded and has query)
  const { data: searchData, loading: searchLoading } = useQuery<
    SearchMediaQuery,
    SearchMediaQueryVariables
  >(SearchMediaDocument, {
    variables: { query: searchQuery, type: mediaType },
    skip: !expanded || !searchQuery.trim(),
  });

  const [linkMediaMutation] = useMutation<LinkMediaMutation, LinkMediaMutationVariables>(
    LinkMediaDocument,
    {
      refetchQueries: sourceMediaId
        ? [{ query: GetLinkedMediaDocument, variables: { mediaId: sourceMediaId } }]
        : [],
      onCompleted: () => {
        toast.success("Media linked successfully");
        refetchLinks();
      },
      onError: (error) => {
        if (error.message.includes("already exists")) {
          toast.error("This media is already linked");
        } else {
          toast.error(`Failed to link media: ${error.message}`);
        }
      },
    }
  );

  const [unlinkMediaMutation] = useMutation<UnlinkMediaMutation, UnlinkMediaMutationVariables>(
    UnlinkMediaDocument,
    {
      refetchQueries: sourceMediaId
        ? [{ query: GetLinkedMediaDocument, variables: { mediaId: sourceMediaId } }]
        : [],
      onCompleted: () => {
        toast.success("Media unlinked successfully");
        refetchLinks();
      },
      onError: (error) => {
        toast.error(`Failed to unlink media: ${error.message}`);
      },
    }
  );

  const handleToggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleLink = async () => {
    if (!sourceMediaId) {
      // For new uploads, just callback with selected IDs
      onLink?.(selectedIds);
      setSelectedIds([]);
      setSearchQuery("");
      return;
    }

    // For existing media, create links via mutation
    try {
      let successCount = 0;
      for (const toId of selectedIds) {
        // Skip if already linked
        if (alreadyLinkedIds.has(toId)) {
          continue;
        }

        await linkMediaMutation({
          variables: {
            input: {
              fromId: sourceMediaId,
              toId,
            },
          },
        });
        successCount++;
      }

      if (successCount > 0) {
        toast.success(
          `Successfully linked ${successCount} ${successCount === 1 ? "item" : "items"}`
        );
      }
    } catch (error) {
      // Error already handled by mutation onError
      console.error("Link error:", error);
    } finally {
      setSelectedIds([]);
      setSearchQuery("");
    }
  };

  const handleUnlink = (linkId: string) => {
    if (!sourceMediaId) {
      onUnlink?.(linkId);
      return;
    }

    setLinkIdToUnlink(linkId);
    setShowUnlinkConfirm(true);
  };

  const handleConfirmUnlink = async () => {
    if (!linkIdToUnlink) return;

    setIsUnlinking(true);
    try {
      await unlinkMediaMutation({
        variables: { linkId: linkIdToUnlink },
      });
    } finally {
      setIsUnlinking(false);
      setShowUnlinkConfirm(false);
      setLinkIdToUnlink(null);
    }
  };

  const handleRemovePendingLink = (mediaId: string) => {
    // For new uploads, remove from currentLinks
    const updatedLinks = currentLinks.filter((id) => id !== mediaId);
    onLink?.(updatedLinks);
  };

  const renderMediaThumbnail = (
    media: SearchMediaQuery["searchMedia"][number],
    size: "sm" | "md" = "md"
  ) => {
    const sizeClasses = size === "sm" ? "h-12 w-12" : "h-16 w-16";

    if (media.__typename === "Image") {
      return (
        <img
          src={media.url}
          alt={media.filename}
          className={`${sizeClasses} rounded object-cover`}
        />
      );
    }

    if (media.__typename === "Video") {
      return (
        <div className={`${sizeClasses} rounded bg-gray-100 flex items-center justify-center`}>
          <FiVideo className="h-6 w-6 text-gray-400" />
        </div>
      );
    }

    if (media.__typename === "Audio") {
      return (
        <div className={`${sizeClasses} rounded bg-gray-100 flex items-center justify-center`}>
          <FiMusic className="h-6 w-6 text-gray-400" />
        </div>
      );
    }

    return (
      <div className={`${sizeClasses} rounded bg-gray-100 flex items-center justify-center`}>
        <FiImage className="h-6 w-6 text-gray-400" />
      </div>
    );
  };

  const mediaTypeLabel = getMediaConfig(mediaType).labelPlural;
  const linkedItems = linkedData?.linkedMedia || [];

  // Filter linked items to show only the target media type
  // Performance: Use type fields directly from the link (no need to query media collections)
  const relevantLinks = linkedItems.filter((link) => {
    const targetMediaType =
      link.fromMediaId === sourceMediaId ? link.toMediaType : link.fromMediaType;
    return targetMediaType === mediaType;
  });

  // Get pending links details (for new uploads)
  const pendingLinks = (pendingLinksData?.searchMedia || []).filter((item) =>
    currentLinks.includes(item.id)
  );

  // Get IDs of already linked media to prevent duplicates
  const alreadyLinkedIds = new Set([
    ...relevantLinks.map((link) =>
      link.fromMediaId === sourceMediaId ? link.toMediaId : link.fromMediaId
    ),
    ...currentLinks, // Also exclude pending links from search results
  ]);

  // Filter search results to exclude already linked items
  const results = (searchData?.searchMedia || []).filter((item) => !alreadyLinkedIds.has(item.id));

  // Total link count (for header badge)
  const totalLinkCount = sourceMediaId ? relevantLinks.length : currentLinks.length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{mediaTypeLabel} links</span>
          {totalLinkCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              {totalLinkCount}
            </span>
          )}
        </div>
        <FiChevronRight
          className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
        />
      </button>

      {/* Content */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          {/* Search Input */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${mediaTypeLabel.toLowerCase()}...`}
                className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Search Results Grid */}
          {searchQuery.trim() && (
            <div>
              {searchLoading ? (
                <div className="text-center py-8 text-sm text-gray-500">Searching...</div>
              ) : results.length > 0 ? (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {results.map((item) => {
                      const isSelected = selectedIds.includes(item.id);
                      return (
                        <div
                          key={item.id}
                          className={`relative group cursor-pointer rounded-lg border-2 transition-all ${
                            isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => handleToggleSelection(item.id)}
                        >
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="absolute top-2 left-2 z-10 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 pointer-events-none"
                          />

                          {/* Thumbnail */}
                          <div className="p-2">
                            <div className="aspect-video w-full overflow-hidden rounded bg-gray-100 flex items-center justify-center">
                              {renderMediaThumbnail(item)}
                            </div>

                            {/* Info */}
                            <p
                              className="mt-2 truncate text-xs font-medium text-gray-900"
                              title={item.filename}
                            >
                              {item.filename}
                            </p>
                            {item.tags && item.tags.length > 0 && (
                              <p className="truncate text-xs text-gray-500">
                                {item.tags.slice(0, 2).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Link Button */}
                  <button
                    type="button"
                    onClick={handleLink}
                    disabled={selectedIds.length === 0}
                    className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Link selected ({selectedIds.length})
                  </button>
                </>
              ) : (
                <div className="text-center py-8 text-sm text-gray-500">
                  No {mediaTypeLabel.toLowerCase()} found
                </div>
              )}
            </div>
          )}

          {/* Linked Items (for existing media) */}
          {sourceMediaId && relevantLinks.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Linked {mediaTypeLabel} ({relevantLinks.length})
              </h4>
              <div className="space-y-2">
                {relevantLinks.map((link) => {
                  const linkedMedia =
                    link.fromMediaId === sourceMediaId ? link.toMedia : link.fromMedia;
                  return (
                    <div
                      key={link.id}
                      className="flex items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
                    >
                      {/* Thumbnail */}
                      {renderMediaThumbnail(linkedMedia, "sm")}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p
                          className="truncate text-sm font-medium text-gray-900"
                          title={linkedMedia.filename}
                        >
                          {linkedMedia.filename}
                        </p>
                        <p className="text-xs text-gray-500">
                          {linkedMedia.__typename === "Audio"
                            ? linkedMedia.audioText
                            : linkedMedia.tags}
                          {/* • {new Date(link.createdAt).toLocaleDateString()} */}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => window.open(linkedMedia.url, "_blank")}
                          className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View media"
                        >
                          <FiExternalLink className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUnlink(link.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Unlink"
                        >
                          <FiX className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pending Links (for new uploads) */}
          {!sourceMediaId && pendingLinks.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Pending {mediaTypeLabel} ({pendingLinks.length})
              </h4>
              <div className="space-y-2">
                {pendingLinks.map((media) => (
                  <div
                    key={media.id}
                    className="flex items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
                  >
                    {/* Thumbnail */}
                    {renderMediaThumbnail(media, "sm")}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate text-sm font-medium text-gray-900"
                        title={media.filename}
                      >
                        {media.filename}
                      </p>
                      <p className="text-xs text-gray-500">{media.__typename} • Pending upload</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => window.open(media.url, "_blank")}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        title="View media"
                      >
                        <FiExternalLink className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemovePendingLink(media.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove link"
                      >
                        <FiX className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={showUnlinkConfirm}
        onClose={() => {
          setShowUnlinkConfirm(false);
          setLinkIdToUnlink(null);
        }}
        onConfirm={handleConfirmUnlink}
        title="Unlink Media"
        message="Are you sure you want to unlink this media?"
        confirmLabel="Unlink"
        variant="warning"
        isLoading={isUnlinking}
      />
    </div>
  );
}
